import { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@mui/material';

interface CheckoutChampPasswordDialogProps {
  open: boolean;
  accountName: string;
  loginId?: string;
  onClose: () => void;
  onSave: (password: string) => Promise<void>;
}

export default function CheckoutChampPasswordDialog({
  open,
  accountName,
  loginId,
  onClose,
  onSave,
}: CheckoutChampPasswordDialogProps) {
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setPassword('');
      setError(null);
    }
  }, [open]);

  const handleSave = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await onSave(password);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update password');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      maxWidth="xs"
      fullWidth
      onClose={(_event, reason) => {
        if (reason === 'backdropClick') return;
        onClose();
      }}
    >
      <DialogTitle>Change password — {accountName}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {loginId && (
          <TextField
            label="Login ID"
            value={loginId}
            fullWidth
            margin="dense"
            slotProps={{ input: { readOnly: true } }}
          />
        )}
        <TextField
          label="New password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          fullWidth
          autoFocus
          margin="dense"
          autoComplete="new-password"
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={!password || submitting}
          sx={{ backgroundColor: '#465fff', '&:hover': { backgroundColor: '#3641f5' } }}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
